// src/layout/components/ingest/IngestGeoHeatmap.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";

// Leaflet CSS
import "leaflet/dist/leaflet.css";

// plugin heat
import "leaflet.heat";

function HeatLayer({
  points = [],
  radius = 34,
  blur = 22,
  maxZoom = 17,
  opacity = 1,
  // 🔥 gradiente forte (amarelo->laranja->vermelho)
  gradient = {
    0.0: "#00b3ff",
    0.25: "#00ff9a",
    0.5: "#ffe600",
    0.75: "#ff7a00",
    1.0: "#ff0033",
  },
  // normalização
  weightMode = "sqrt", // "linear" | "sqrt" | "log"
  clampMin = 0.15,
  clampMax = 1.0,
  max = 1.0, // escala do heatmap (ajuste junto com os pesos)
}) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // ✅ normaliza z-index dos panes (evita “vazar” por cima da bottom bar)
    const setPaneZ = (name, z) => {
      const p = map.getPane(name);
      if (p) p.style.zIndex = String(z);
    };
    setPaneZ("tilePane", 200);
    setPaneZ("overlayPane", 300);
    setPaneZ("shadowPane", 400);
    setPaneZ("markerPane", 500);
    setPaneZ("tooltipPane", 650);
    setPaneZ("popupPane", 700);

    // remove layer anterior
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }

    const arr = Array.isArray(points) ? points : [];
    if (!arr.length) return;

    // 1) extrai w
    const raw = arr
      .map((p) => Number(p.w ?? 1))
      .filter((w) => Number.isFinite(w) && w > 0);

    if (!raw.length) return;

    // 2) calcula referência de escala (p95) pra dar contraste e evitar outliers dominarem
    const sorted = [...raw].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)] || sorted[sorted.length - 1] || 1;

    function boostWeight(w) {
      const x = Math.max(0, w / (p95 || 1)); // normaliza ~0..1 (p95 vira ~1)
      let y = x;
      if (weightMode === "log") y = Math.log1p(9 * x) / Math.log1p(9); // comprime e realça médios
      else if (weightMode === "sqrt") y = Math.sqrt(x);
      // clamp
      y = Math.max(clampMin, Math.min(clampMax, y));
      return y;
    }

    const heatPts = arr
      .map((p) => [Number(p.lat), Number(p.lng), boostWeight(Number(p.w ?? 1))])
      .filter((x) => Number.isFinite(x[0]) && Number.isFinite(x[1]) && Number.isFinite(x[2]) && x[2] > 0);

    if (!heatPts.length) return;

    // ✅ pane dedicado pro heat, com z-index controlado
    const paneName = "heatPane";
    if (!map.getPane(paneName)) map.createPane(paneName);
    const pane = map.getPane(paneName);
    if (pane) {
      pane.style.opacity = String(opacity);
      pane.style.zIndex = "350"; // ✅ overlay baixo
    }

    const layer = L.heatLayer(heatPts, {
      radius,
      blur,
      maxZoom,
      gradient,
      // deixa sempre visível mesmo em valores baixos
      minOpacity: 0.35,
      // controla escala (padrão é 1)
      max,
      // ✅ garante pane
      pane: paneName,
    });

    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        layerRef.current.remove();
        layerRef.current = null;
      }
    };
  }, [map, points, radius, blur, maxZoom, opacity, gradient, weightMode, clampMin, clampMax, max]);

  return null;
}

function FitBoundsOnData({ points = [], enabled = true }) {
  const map = useMap();
  const lastFitKeyRef = useRef("");

  useEffect(() => {
    if (!enabled) return;
    if (!map) return;

    const arr = Array.isArray(points) ? points : [];
    if (!arr.length) return;

    // evita “fitBounds” repetindo sem necessidade
    const key = `${arr.length}:${arr[0]?.lat}:${arr[0]?.lng}:${arr[arr.length - 1]?.lat}:${arr[arr.length - 1]?.lng}`;
    if (key === lastFitKeyRef.current) return;
    lastFitKeyRef.current = key;

    const ll = arr
      .map((p) => [Number(p.lat), Number(p.lng)])
      .filter((x) => Number.isFinite(x[0]) && Number.isFinite(x[1]));

    if (ll.length < 2) return;

    const bounds = L.latLngBounds(ll.map((x) => L.latLng(x[0], x[1])));
    map.fitBounds(bounds.pad(0.15), { animate: true });
  }, [map, points, enabled]);

  return null;
}

export default function IngestGeoHeatmap({
  points = [],
  theme,
  height = 320,
  initialCenter = [-15.78, -47.93], // Brasil (fallback)
  initialZoom = 4,
}) {
  const [autoFit, setAutoFit] = useState(true);
  const [radius, setRadius] = useState(26);
  const [blur, setBlur] = useState(18);
  const [opacity, setOpacity] = useState(0.85);

  const ui = useMemo(() => {
    return {
      text: theme?.text ?? "white",
      muted: theme?.muted ?? "rgba(255,255,255,0.72)",
      border: theme?.border ?? "rgba(255,255,255,0.10)",
      panel: theme?.panel ?? "rgba(255,255,255,0.06)",
      chipBg: "rgba(255,255,255,0.08)",
      chipOnBg: "rgba(255,255,255,0.14)",
    };
  }, [theme]);

  const Chip = ({ active, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: `1px solid ${ui.border}`,
        background: active ? ui.chipOnBg : ui.chipBg,
        color: active ? ui.text : ui.muted,
        borderRadius: 999,
        padding: "6px 10px",
        fontSize: 12,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {children}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Controls (mantive sua estrutura; pode reativar Chips depois) */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {/* exemplos (opcional)
        <Chip active={autoFit} onClick={() => setAutoFit((v) => !v)}>
          Auto-fit
        </Chip>
        <Chip active={radius === 26} onClick={() => setRadius(26)}>
          Raio 26
        </Chip>
        */}

        <span style={{ marginLeft: "auto", color: ui.muted, fontSize: 12 }}>
          {Array.isArray(points) ? points.length : 0} bins
        </span>
      </div>

      {/* Map */}
      <div
        style={{
          height,
          border: `1px solid ${ui.border}`,
          background: ui.panel,
          borderRadius: 16,
          overflow: "hidden",

          // ✅ cria stacking context isolado pro Leaflet não “vazar”
          position: "relative",
          zIndex: 0,
          isolation: "isolate",
        }}
      >
        <MapContainer
          center={initialCenter}
          zoom={initialZoom}
          preferCanvas
          style={{
            height: "100%",
            width: "100%",
            position: "relative",
            zIndex: 0,
          }}
        >
          {/* tiles simples e gratuitos (OSM) */}
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          <HeatLayer
            points={points}
            radius={radius}
            blur={blur}
            opacity={opacity}
            // mantém seus defaults, mas agora com panes “domados”
          />
          <FitBoundsOnData points={points} enabled={autoFit} />
        </MapContainer>
      </div>

      <div style={{ color: ui.muted, fontSize: 12, lineHeight: "16px" }}>
        Dica: use o zoom/pan do mapa livremente — os dados já respeitam a janela de tempo selecionada acima.
      </div>
    </div>
  );
}
